import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Input, Button, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell } from '@nextui-org/react';
import React from 'react';

import { ServerUUID, ServerName, ServerHost, ClusterTag } from '@common/types';
import { ModernModal } from '@portal/components/common/ModernModal.ui';
import { PasswordInput } from '@portal/components/common/PasswordInput.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';

interface BatchSslPreviewModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  clusterId: string;
  impactedServers: Array<{
    uuid: ServerUUID;
    name: ServerName;
    host: ServerHost;
    clusterTag: ClusterTag;
  }>;
  totalServers: number;
  sslEnabledCount: number;
  username: string;
  password: string;
  onUsernameChange: (val: string) => void;
  onPasswordChange: (val: string) => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const BatchSslPreviewModal: React.FC<BatchSslPreviewModalProps> = ({
  isOpen,
  onOpenChange,
  clusterId,
  impactedServers,
  totalServers,
  sslEnabledCount,
  username,
  password,
  onUsernameChange,
  onPasswordChange,
  onConfirm,
  isLoading,
}) => {
  const icon = <ShieldCheckIcon className='h-4 w-4' />;
  const subtitle = `Cluster: ${clusterId}`;

  const footer = (
    <>
      <Button color='danger' variant='light' onPress={onOpenChange} disabled={isLoading}>
        Cancel
      </Button>
      <Button color='primary' onPress={onConfirm} isLoading={isLoading} isDisabled={!username || !password}>
        Update {sslEnabledCount} Servers
      </Button>
    </>
  );

  return (
    <ModernModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title='Batch SSL Password Update'
      subtitle={subtitle}
      icon={icon}
      size='2xl'
      footer={footer}
    >
      <div className='flex flex-col gap-3'>
        {/* Summary Section */}
        <div className='bg-blue-50 p-3 rounded-lg'>
          <TypographyUI className='text-sm font-medium text-blue-800 mb-2'>Summary</TypographyUI>
          <div className='flex items-center gap-6 text-sm'>
            <div className='flex items-center gap-2'>
              <TypographyUI className='text-gray-600'>Total servers:</TypographyUI>
              <TypographyUI className='font-medium'>{totalServers}</TypographyUI>
            </div>
            <div className='flex items-center gap-2'>
              <TypographyUI className='text-gray-600'>SSL enabled:</TypographyUI>
              <TypographyUI className='font-medium text-green-600'>{sslEnabledCount}</TypographyUI>
            </div>
          </div>
        </div>

        {/* Impacted Servers Table */}
        <div>
          <TypographyUI className='text-sm font-medium mb-2'>Servers that will be updated:</TypographyUI>
          <div className='max-h-48 overflow-auto'>
            <Table aria-label='Impacted servers list'>
              <TableHeader>
                <TableColumn>Server Name</TableColumn>
                <TableColumn>Host</TableColumn>
                <TableColumn>Cluster</TableColumn>
              </TableHeader>
              <TableBody>
                {impactedServers.map(server => (
                  <TableRow key={server.uuid}>
                    <TableCell>
                      <TypographyUI className='font-medium'>{server.name}</TypographyUI>
                    </TableCell>
                    <TableCell>
                      <TypographyUI className='text-gray-600'>{server.host}</TypographyUI>
                    </TableCell>
                    <TableCell>
                      <TypographyUI className='text-gray-600'>{server.clusterTag}</TypographyUI>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* SSL Credentials */}
        <div className='space-y-3'>
          <TypographyUI className='text-sm font-medium'>SSL Credentials</TypographyUI>
          <Input label='SSL Username' value={username} isDisabled variant='bordered' />
          <PasswordInput
            label='SSL Password'
            placeholder='Enter SSL password'
            value={password}
            onValueChange={onPasswordChange}
            isRequired
            variant='bordered'
          />
        </div>

        {/* Warning */}
        <div className='bg-yellow-50 p-3 rounded-lg'>
          <TypographyUI className='text-sm text-yellow-800'>
            ⚠️ This action will update SSL passwords for {sslEnabledCount} servers in the &quot;{clusterId}&quot;
            cluster. This operation cannot be undone.
          </TypographyUI>
        </div>
      </div>
    </ModernModal>
  );
};
